<template>
  <div
    class="fts-phone-number"
    :class="{'comfort-design': comfortDesign}"
  >
    <fts-select
      class="country-dropdown custom"
      :formData="phone"
      :item="{fieldName: 'code'}"
      :options="countries"
      :clearable="false"
      reduceKey="code"
      popperWidth="250px"
      @input="selected"
    >
      <div slot="selected-option-container" slot-scope="{option}" class="fts-flag" :class="`flag-${option.id}`"></div>
      <font-awesome-icon slot="open-indicator" slot-scope="{attributes}" v-bind="attributes" :icon="['fas', 'angle-down']" class="select-open-indicator" />
      <div slot="option" slot-scope="{option}" class="country-option">
        <div class="fts-flag country-flag" :class="`flag-${option.id}`"></div>
        <div class="country-name">{{option.name}}</div>
        <div class="country-dial-code">{{option.dialCode}}</div>
      </div>
    </fts-select>
    <div class="phone-number__dial-code">{{phone.dialCode}}</div>
    <cleave
      class="fts-form-control phone-number__input"
      :class="{'form-control--error': error}"
      v-model="model"
      :options="options"
      autocomplete="ftsPhoneNumberInput"
      @blur="onBlur"
      ref="ftsPhoneNumberInput" />
  </div>
</template>

<script>
import Vue from 'vue';
import countriesList from "../../json/countries.json";
import { clientStore } from "../store/clientStore";

import parsePhoneNumber from 'libphonenumber-js'
import Cleave from "vue-cleave-component";
import "cleave.js/dist/addons/cleave-phone.i18n.js";
Vue.use(Cleave);

import FTSSelect from "./FTSSelect";

export default {
  components: {
    "fts-select": FTSSelect
  },
  props: {
    item: Object,
    formData: Object,
    preferredCountries: Array,
    excludeCountries: Array,
    comfortDesign: Boolean,
    error: Boolean
  },
  data: () => ({
    phone: {
      code: 'SG',
      dialCode: '+65'
    },
    preferredCountry: 'sg',
    countriesList: countriesList
  }),
  methods: {
    selected(e) {
      const res = this.countries.find(x => x.code === e);
      if (res && res.dialCode) {
        this.$set(this.phone, 'dialCode', res.dialCode);
        this.$set(this.phone, 'code', res.code);
      }
      if (this.model) {
        this.onBlur();
      }
      /* focus cleave input */
      this.$nextTick(() => {
        this.$refs.ftsPhoneNumberInput.$el.focus();
      })
    },
    onBlur() {
      this.$emit('blur');
    }
  },
  computed: {
    countries() {
      let countries = _.cloneDeep(countriesList);
      /* exclude */
      if (Array.isArray(this.excludeCountries)) {
        countries = countries.filter(x => {
          return x.code && !this.excludeCountries.includes(x.id)
        });
      }
      /* preferred countries */
      let preferredCountries = [];
      if (this.preferredCountry) {
        preferredCountries = [this.preferredCountry];
      }
      if (Array.isArray(this.preferredCountries)) {
        preferredCountries = [
          ...preferredCountries,
          ...this.preferredCountries
        ];
      }
      preferredCountries = [...new Set(preferredCountries)];
      if (preferredCountries) {
        preferredCountries.reverse().forEach(x => {
          const country = countries.find(y => y.id === x)
          if (country && country.id) {
            countries.splice(countries.indexOf(country), 1);
            countries.unshift(country);
          }
        });
      }
      return countries;
    },
    model: {
      get() {
        let phoneNumber = this.formData[this.item.fieldName];

        if (typeof phoneNumber === 'number') {
          phoneNumber = String(phoneNumber);
        } else if (typeof phoneNumber !== 'string' || !phoneNumber.trim()) {
          console.warn(`Unsupported data type. Expected a phone number, but received ${typeof phoneNumber}.`);
          return null;
        }

        try {
          if (phoneNumber) {
            const res = parsePhoneNumber(phoneNumber);
            if (res) {
              /* set dial code */
              if (res.countryCallingCode)
                this.$set(this.phone, 'dialCode', `+${res.countryCallingCode}`);
              /* set code (country short name) */
              if (res.country) 
                this.$set(this.phone, 'code', res.country);
              /* phone number (without dial code) */
              return res.nationalNumber;
            } else {
              const firstChar = phoneNumber.replaceAll(' ','').charAt(0);
              const nationalNumber = phoneNumber.replaceAll(' ','').replaceAll('+', '');
              if (firstChar === '+' && this.phone.dialCode) {
                return (`+${nationalNumber}`).replaceAll(this.phone.dialCode, '');
              }
              return nationalNumber;
            }
          }
        } catch(err) {
          console.error(err);
        }
      },
      set(val) {
        if (val)
          this.$set(this.formData, this.item.fieldName, this.phone.dialCode + val);
        else if (this.formData[this.item.fieldName])
          this.$delete(this.formData, this.item.fieldName);
      }
    },
    options() {
      let options = {
        phone: true, 
        phoneRegionCode: this.phone.code,
        delimiter: ''
      }

      if (this.phone.code === 'CI') {
        options.phoneRegionCode = '';
      }

      return options;
    }
  },
  watch: {
    model(val, oldVal) {
      if (val !== oldVal) {
        this.$emit('input', val);
      }
    }
  },
  created() {
    const setup = (country) => {
      const res = countriesList.find(x => x.name === country);
      if (res) {
        this.phone = { code: res.code, dialCode: res.dialCode };
        this.preferredCountry = res.id;
      }
    }
    if (this.item && this.item.country) {
      setup(this.item.country);
    } else if (clientStore.getters.workspaceCountry) {
      setup(clientStore.getters.workspaceCountry);
    }
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-phone-number {
  position: relative;
  $country-dropdown-width: 55px;
  $dial-code-width: 40px;
  .phone-number__input {
    padding-left: calc(#{$country-dropdown-width + $dial-code-width});
  }
  .country-dropdown {
    position: absolute;
    left: 0;
    height: 100%;
    max-width: $country-dropdown-width;
    min-width: $country-dropdown-width;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    &.vs--open {
      .selected-option {
        opacity: .4;
      }
    }
    &.vs--searching {
      .selected-option {
        display: none;
      }
    }
    .vs__dropdown-toggle {
      height: 100%;
      padding: 0 0 0 0.75rem;
      border: 0;
      background-color: transparent;
      cursor: pointer;
    }
    .vs__selected-options {
      .selected-option {
        @include absolute-center-y;
      }
      .vs__search {
        /* old class override */
        min-width: 0 !important;
        margin: 0;
        padding: 0;
        font-size: var(--inistate-label-text-14);
        line-height: var(--inistate-paragraph-text-14--line-height);
      }
    }
    .vs__actions {
      padding: 0;
      .select-open-indicator {
        color: var(--fts-gray-400);
      }
    }
  }
  .phone-number__dial-code {
    position: absolute;
    left: $country-dropdown-width;
    height: 100%;
    width: $dial-code-width;
    display: inline-flex;
    align-items: center;
    justify-content: end;
    font-size: var(--inistate-label-text-14);
    line-height: var(--inistate-paragraph-text-14--line-height);
    color: var(--fts-form-control);
  }
  &.comfort-design {
    $country-dropdown-width: 55px;
    $dial-code-width: 50px;
    .phone-number__input {
      padding-left: calc(#{$country-dropdown-width + $dial-code-width});
    }
    .country-dropdown {
      max-width: $country-dropdown-width;
      min-width: $country-dropdown-width;
    }
    .phone-number__dial-code {
      width: $dial-code-width;
      font-size: $comfort-font-size;
    }
  }
}
/* country option */
.country-option {
  min-width: 0;
  display: flex;
  align-items: center;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
  .country-flag {
    flex-shrink: 0;
  }
  .country-name {
    @include text-overflow-ellipsis;
    margin-left: 0.375rem;
    margin-right: 0.375rem;
  }
  .country-dial-code {
    font-size: var(--inistate-label-text-12);
    line-height: var(--inistate-paragraph-text-12--line-height);
    color: var(--fts-default);
  }
}
</style>